import React, { useState, useEffect } from 'react';
import {
  Menu, X, Moon, Sun, Github, Linkedin, Mail, ExternalLink,
  Plane, Radio, Mountain, Smartphone, ShieldCheck,
  Trophy, Medal, Award, ChevronLeft, ChevronRight,
} from 'lucide-react';

export default function Portfolio() {
  const [darkMode, setDarkMode] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [animateProjects, setAnimateProjects] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [awardIndex, setAwardIndex] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);

      const projectsSection = document.getElementById('projects');
      if (projectsSection) {
        const rect = projectsSection.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.85) {
          setAnimateProjects(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const awards = [
    {
      icon: Trophy,
      color: '#d4af37',
      image: '/assets/drone-compet.JPG',
      place: '1st Place + Best Presenter',
      event: 'Comms-Denied Autonomy · Red Team Hackathon Series',
      detail: 'Toronto · Jun 2026 · $10,000 won',
    },
    {
      icon: Medal,
      color: '#9ca3af',
      image: '/assets/peek-voyageurs.jpg',
      place: '2nd Place',
      event: "Voyageurs' Innovation Challenge · Laurentian University",
      detail: 'Sudbury · 2026 · $11,500 prize pool',
    },
    {
      icon: Award,
      color: '#b87333',
      image: '/assets/provenance.png',
      place: '3rd Place',
      event: 'Verified Canadian Supply Chains · Red Team Hackathon Series',
      detail: 'Ottawa · May 2026',
    },
  ];

  const experience = [
    {
      role: 'Software Engineer (Co-op)',
      org: 'Jannatec Technologies',
      period: 'May 2026 to Present',
      location: 'Sudbury, ON',
      detail: 'C and firmware on ESP microcontrollers; Android driver interface for the JAWS proximity-detection system; infrastructure-free peer-to-peer RF platform.',
    },
    {
      role: 'Student Assistant',
      org: 'NOSM University',
      period: 'Sep 2025 to Apr 2026',
      location: 'Sudbury, ON',
      detail: 'Python/Playwright automation replacing bulk manual data entry. 90% time reduction at 100% accuracy.',
    },
    {
      role: 'Co-Founder & Software Engineer',
      org: 'Bluenest',
      period: 'Jan 2026 to Mar 2026',
      location: 'Sudbury, ON',
      detail: 'Technical architecture and banking API integration for a fintech startup in the City of Sudbury incubator. Project pivoted.',
    },
    {
      role: 'IT Intern',
      org: 'Discovery Silver Corp. (TSX:DSV)',
      period: 'May 2025 to Sep 2025',
      location: 'Timmins, ON',
      detail: 'Built a centralized IoT emergency alert system; restored mission-critical fiber connectivity; Cisco deployment and Wireshark packet analysis cutting latency 15%.',
    },
  ];

  // Ordered by relevance to autonomy work, not chronology.
  const projects = [
    {
      title: 'Autonomous Drone: Comms-Denied Navigation',
      short: 'Autonomous Drone',
      meta: 'Red Team Hackathon Series · York University · Jun 2026',
      icon: Plane,
      image: '/assets/drone.png',
      link: 'https://www.linkedin.com/feed/update/urn:li:activity:7474248651842793473/',
      headline: true,
      tags: ['Python', 'Computer Vision', 'SLAM', 'VLM', 'Real-Time Control', 'Autonomous Navigation'],
      blurb: "Autonomous flight through a full course in a GPS & comms-denied environment, generating joystick-level control from a live video feed and telemetry. Runs on-device with SLAM for localization and mapping (loop closure to correct drift on revisit) plus a VLM for multi-modal scene interpretation, using a hover-and-recheck policy for low-confidence states.",
    },
    {
      title: 'JAWS / SmartView: Underground Proximity Warning',
      short: 'JAWS / SmartView',
      meta: 'Jannatec Technologies · Software Engineer (Co-op) · May 2026 to Present',
      icon: Radio,
      image: 'https://mineconnect.com/wp-content/uploads/2024/07/Jannatec-3-news.jpg',
      link: 'https://www.jannatec.com/PDFs/Jannatec-JAWS.pdf',
      tags: ['C', 'Embedded Firmware', 'ESP Microcontrollers', 'RF Communication', 'Android'],
      blurb: "Real-time proximity-warning system for underground mines, built on an infrastructure-free peer-to-peer RF mesh across ESP microcontroller nodes. and standalone proximity sensors. I engineered performance-critical firmware in C and the Android driver interface; shipping in production underground.",
    },
    {
      title: 'Golden Wind: Mine Stope Optimizer',
      short: 'Golden Wind',
      meta: 'Underground mine planning tool · Jun 2025',
      icon: Mountain,
      image: '/assets/mine_stope.png',
      link: 'https://github.com/Psoro77/',
      tags: ['Python', 'Numba (JIT)', 'C++', 'PySide6', 'PyVista', 'NumPy', 'SciPy'],
      blurb: "A Free and Open-Source underground stope optimizer that computes 931 economic stopes in around 0.5s, down from hours, by adapting computer-vision algorithms to mine planning and JIT-compiling the hot path. Ships as a desktop app with live parameter tuning, 3D block-model visualization, and DXF export.",
    },
    {
      title: 'Peek: Grocery Price Comparison',
      short: 'Peek',
      meta: "Voyageurs' Innovation Challenge · Founder · Aug 2025 to Present",
      icon: Smartphone,
      image: '/assets/mobileapp.png',
      link: 'https://peekeat.app',
      linkLabel: 'peekeat.app',
      tags: ['React Native', 'Python', 'Supabase', 'PostgreSQL', 'Docker'],
      blurb: "Cross-platform React Native app for cross-store grocery price comparison, recipe discovery, and budget tracking, backed by weekly Python scraping pipelines into a Supabase database. Runs on a Dockerized production server; live on the iOS App Store and Google Play.",
    },
    {
      title: 'Verified Canadian Supply Chains: Cryptographic Provenance',
      short: 'Verified Supply Chains',
      meta: 'Red Team Hackathon Series · Ottawa · May 2026',
      icon: ShieldCheck,
      image: '/assets/provenance.png',
      link: 'https://redteamhack.ca/winners',
      tags: ['Backend', 'Cryptographic Attestation', 'Algorithm Design'],
      blurb: "Backend and core algorithm for verifying whether a product qualifies as \"Made in Canada\" across a multi-tier supply chain of untrusted suppliers. Chains cryptographic attestations into a tamper-evident chain of custody resistant to tampering, forgery, and replay.",
    },
  ];

  const otherWork = [
    { name: 'HTTP Server', note: '12K req/s across 10K connections (C10k) at sub-5ms, C++17 on raw POSIX sockets', link: 'https://github.com/Psoro77' },
    { name: 'Apache Airflow', note: 'Merged UI fixes upstream in apache/airflow', link: 'https://github.com/apache/airflow/pulls?q=is%3Apr+author%3APsoro77' },
    { name: 'TensorTrend', note: 'ML market prediction, C++/Qt desktop frontend, sub-250ms inference', link: 'https://github.com/Psoro77/tensortrend' },
    { name: 'Mythos', note: 'ConUHacks X, FastAPI, knowledge graphs, real-time voice', link: 'https://github.com/Psoro77/ConUHacks-X' },
    { name: 'whisper-local-mac', note: 'Local Whisper speech-to-text', link: 'https://github.com/Psoro77/' },
    { name: 'Flower Shop', note: 'PHP/MySQL e-commerce', link: 'https://github.com/Psoro77/Learning-Web-page-Basics/tree/main/projet3' },
  ];

  const accent = darkMode ? '#7c93ff' : '#667eea';

  const prevAward = () => setAwardIndex(i => (i - 1 + awards.length) % awards.length);
  const nextAward = () => setAwardIndex(i => (i + 1) % awards.length);

  return (
    <div className={`${darkMode ? 'dark' : ''}`}>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        /* Fonts are loaded via <link> in public/index.html. An @import is invalid
           this far into a stylesheet and was being silently dropped. */

        html {
          scroll-behavior: smooth;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          background: ${darkMode ? '#0f0f0f' : '#ffffff'};
          color: ${darkMode ? '#e0e0e0' : '#333333'};
          transition: background 0.3s, color 0.3s;
        }

        /* ─── Navbar ─── */
        .navbar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          background: ${darkMode ? 'rgba(15, 15, 15, 0.95)' : 'rgba(255, 255, 255, 0.95)'};
          backdrop-filter: blur(12px);
          border-bottom: 1px solid ${darkMode ? '#2a2a2a' : '#e8e8e8'};
          padding: 1rem 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: box-shadow 0.3s;
          ${scrolled ? `box-shadow: 0 2px 20px rgba(0,0,0,${darkMode ? '0.4' : '0.08'});` : ''}
        }

        .navbar-logo {
          font-size: 1.5rem;
          font-weight: bold;
          color: ${darkMode ? '#ffffff' : '#333333'};
          text-decoration: none;
          font-family: 'Le Murmure', cursive;
        }

        .nav-links {
          display: flex;
          list-style: none;
          gap: 2.25rem;
          align-items: center;
          margin: 0;
          font-family: 'Courier Prime', monospace;
          font-weight: 600;
        }

        .nav-links a {
          text-decoration: none;
          color: ${darkMode ? '#e0e0e0' : '#333333'};
          font-weight: 500;
          font-size: 1.1rem;
          transition: all 0.3s ease;
          position: relative;
        }

        .nav-links a::after {
          content: '';
          position: absolute;
          bottom: -5px;
          left: 0;
          width: 0;
          height: 2px;
          background: ${accent};
          transition: width 0.3s ease;
        }

        .nav-links a:hover {
          color: ${accent};
          transform: translateY(-2px);
        }

        .nav-links a:hover::after {
          width: 100%;
        }

        .theme-btn {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1.5rem;
          padding: 0.5rem;
          display: flex;
          align-items: center;
        }

        .mobile-menu-btn {
          display: none;
          background: none;
          border: none;
          cursor: pointer;
          color: ${darkMode ? '#e0e0e0' : '#333333'};
        }

        @media (max-width: 768px) {
          .nav-links {
            display: none;
            position: absolute;
            top: 60px;
            left: 0;
            right: 0;
            flex-direction: column;
            gap: 1rem;
            padding: 2rem;
            background: ${darkMode ? '#1a1a1a' : '#f5f5f5'};
            border-bottom: 1px solid ${darkMode ? '#2a2a2a' : '#e0e0e0'};
          }

          .nav-links.mobile-open {
            display: flex;
          }

          .mobile-menu-btn {
            display: flex;
          }
        }

        /* ─── Animations ─── */
        @keyframes typing {
          0% { width: 0; }
          100% { width: 100%; }
        }

        @keyframes blink {
          50% { border-right-color: transparent; }
        }

        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(102,126,234,0.3), 0 8px 24px rgba(0,0,0,0.2); }
          50% { box-shadow: 0 0 40px rgba(102,126,234,0.55), 0 8px 24px rgba(0,0,0,0.2); }
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ─── Hero ─── */
        .hero {
          margin-top: 70px;
          padding: 4rem 2rem 3.5rem;
          text-align: center;
          min-height: calc(100vh - 70px);
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          background: ${darkMode
          ? 'linear-gradient(-45deg, #0f0f0f, #1a1a2e, #16213e, #1a1a1a)'
          : 'linear-gradient(-45deg, #f8f9fa, #eef0ff, #f0f4ff, #e9ecef)'};
          background-size: 400% 400%;
          animation: gradientShift 16s ease infinite;
          position: relative;
        }

        .profile-picture {
          width: 220px;
          height: 220px;
          border-radius: 50%;
          object-fit: cover;
          margin-bottom: 1.75rem;
          border: 4px solid ${accent};
          animation: glow 3s ease-in-out infinite;
        }

        .hero h1 {
          font-size: 4rem;
          font-weight: 800;
          margin-bottom: 1rem;
          color: ${darkMode ? '#ffffff' : '#333333'};
          font-family: 'Le Murmure', cursive;
          letter-spacing: 2px;
        }

        .typing-text {
          display: inline-block;
          border-right: 3px solid ${darkMode ? '#ffffff' : '#333333'};
          animation: typing 2.2s steps(25, end), blink 0.7s infinite;
          overflow: hidden;
          white-space: nowrap;
          min-width: 340px;
        }

        .hero .subtitle {
          font-size: 1.5rem;
          font-weight: 600;
          color: ${darkMode ? '#ffffff' : '#333333'};
          margin-bottom: 0.75rem;
        }

        .hero-focus {
          font-size: 1.05rem;
          color: ${accent};
          font-family: 'Courier Prime', monospace;
          font-weight: 700;
          letter-spacing: 0.3px;
          margin-bottom: 1.25rem;
          max-width: 640px;
        }

        .hero-line {
          max-width: 620px;
          font-size: 1.15rem;
          line-height: 1.7;
          color: ${darkMode ? '#b8b8b8' : '#555'};
        }

        /* ─── Awards ─── */
        .awards-section {
          padding: 5rem 2rem 6rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .awards-section .section-heading {
          font-size: 2.5rem;
        }

        .awards-carousel {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          max-width: 900px;
          margin: 2.5rem auto 0;
        }

        .awards-viewport {
          flex: 1;
          overflow: hidden;
          border-radius: 16px;
        }

        .awards-track {
          display: flex;
          transition: transform 0.5s ease;
        }

        .award-slide {
          min-width: 100%;
          display: flex;
          background: ${darkMode ? 'linear-gradient(145deg,#1a1a1a,#1f1f1f)' : '#f8f8f8'};
          border: 1px solid ${darkMode ? '#2f2f2f' : '#e6e6e6'};
          border-radius: 16px;
          overflow: hidden;
        }

        .award-slide-image {
          width: 42%;
          min-height: 300px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .award-slide-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .award-slide-image.placeholder {
          flex-direction: column;
          gap: 0.6rem;
          background: ${darkMode
          ? 'linear-gradient(135deg, #1c1c28, #232338)'
          : 'linear-gradient(135deg, #eef0ff, #e4e8fb)'};
          color: ${darkMode ? '#5b6ab0' : '#a9b3e8'};
        }

        .award-slide-body {
          flex: 1;
          padding: 2rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 0.35rem;
        }

        .award-nav {
          background: none;
          border: 1px solid ${darkMode ? '#2f2f2f' : '#e2e2e2'};
          border-radius: 50%;
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: ${darkMode ? '#e0e0e0' : '#555'};
          flex-shrink: 0;
          transition: all 0.25s;
        }

        .award-nav:hover {
          border-color: ${accent};
          color: ${accent};
        }

        .awards-dots {
          display: flex;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 1.25rem;
        }

        .award-dot {
          width: 9px;
          height: 9px;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          padding: 0;
          background: ${darkMode ? '#333' : '#d0d0d0'};
          transition: all 0.25s;
        }

        .award-dot.active {
          background: ${accent};
          transform: scale(1.25);
        }

        .award-place {
          font-weight: 800;
          font-size: 1.15rem;
          color: ${darkMode ? '#f0f0f0' : '#1a1a1a'};
          margin-bottom: 0.2rem;
        }

        .award-event {
          font-size: 0.95rem;
          line-height: 1.5;
          color: ${darkMode ? '#a8a8a8' : '#555'};
        }

        .award-detail {
          font-size: 0.8rem;
          color: ${accent};
          font-family: 'Courier Prime', monospace;
          font-weight: 700;
          margin-top: 0.5rem;
        }

        .hero-actions {
          display: flex;
          gap: 1rem;
          align-items: center;
          margin-top: 2.25rem;
          flex-wrap: wrap;
          justify-content: center;
        }

        /* ─── Section headings ─── */
        .section-heading {
          text-align: center;
          margin-bottom: 0.75rem;
          color: ${darkMode ? '#ffffff' : '#333333'};
        }

        .section-heading::after {
          content: '';
          display: block;
          width: 50px;
          height: 3px;
          background: ${accent};
          margin: 0.75rem auto 0;
          border-radius: 2px;
        }

        .section-subtitle {
          text-align: center;
          color: ${darkMode ? '#888' : '#999'};
          font-size: 0.95rem;
          font-family: 'Courier Prime', monospace;
          margin-bottom: 3rem;
        }

        /* ─── Experience ─── */
        .experience-section {
          padding: 5rem 2rem;
          max-width: 900px;
          margin: 0 auto;
        }

        .experience-section .section-heading {
          font-size: 2.5rem;
        }

        .experience-list {
          display: flex;
          flex-direction: column;
          gap: 0;
          border-left: 2px solid ${darkMode ? '#2c2c2c' : '#e8e8e8'};
          padding-left: 2rem;
          margin-left: 0.5rem;
        }

        .experience-item {
          position: relative;
          padding-bottom: 2.25rem;
        }

        .experience-item:last-child {
          padding-bottom: 0;
        }

        .experience-item::before {
          content: '';
          position: absolute;
          left: calc(-2rem - 7px);
          top: 0.45rem;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: ${darkMode ? '#0f0f0f' : '#ffffff'};
          border: 2px solid ${accent};
        }

        .experience-role {
          font-size: 1.1rem;
          font-weight: 700;
          color: ${darkMode ? '#f0f0f0' : '#1a1a1a'};
        }

        .experience-org {
          color: ${accent};
        }

        .experience-period {
          font-size: 0.8rem;
          font-family: 'Courier Prime', monospace;
          color: ${darkMode ? '#777' : '#999'};
          margin: 0.25rem 0 0.55rem;
        }

        .experience-detail {
          font-size: 0.95rem;
          line-height: 1.65;
          color: ${darkMode ? '#a8a8a8' : '#555'};
        }

        /* ─── Projects ─── */
        .projects-section {
          padding: 5rem 2rem;
          padding-top: 7rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .projects-section .section-heading {
          font-size: 3rem;
        }

        .projects-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 2rem;
          justify-content: center;
          margin-bottom: 4rem;
          max-width: 1200px;
          margin-left: auto;
          margin-right: auto;
        }

        .project-card {
          background: ${darkMode ? 'linear-gradient(145deg,#1a1a1a,#1e1e1e)' : '#f8f8f8'};
          border-radius: 16px;
          overflow: hidden;
          border: 2px solid ${darkMode ? '#3a3a3a' : '#e0e0e0'};
          transition: border-color 0.3s, box-shadow 0.3s, transform 0.3s;
          text-decoration: none;
          color: inherit;
          display: flex;
          flex-direction: column;
          opacity: 0;
          transform: translateY(30px);
          width: calc(50% - 1rem);
          min-width: 340px;
        }

        a.project-card {
          cursor: pointer;
        }

        .project-card.animate {
          animation: fadeUp 0.5s ease-out forwards;
        }

        a.project-card:hover {
          transform: translateY(-8px);
          border-color: ${accent};
          box-shadow: 0 12px 28px rgba(102, 126, 234, 0.2);
        }

        /* Headline project spans the full row and lays out side-by-side. */
        .project-card.headline {
          width: 100%;
          border-color: ${darkMode ? 'rgba(124,147,255,0.4)' : 'rgba(102,126,234,0.35)'};
        }

        @media (min-width: 900px) {
          .project-card.headline {
            flex-direction: row;
          }

          .project-card.headline .project-image {
            width: 42%;
            height: auto;
            min-height: 340px;
            flex-shrink: 0;
          }

          .project-card.headline .project-body {
            flex: 1;
          }
        }

        .project-image {
          width: 100%;
          height: 220px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${darkMode ? '#2a2a2a' : '#e9ecef'};
          overflow: hidden;
          position: relative;
        }

        .project-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.4s ease;
        }

        a.project-card:hover .project-image img {
          transform: scale(1.04);
        }

        /* Placeholder shown until a real screenshot lands in public/assets/ */
        .project-image.placeholder {
          background: ${darkMode
          ? 'linear-gradient(135deg, #1c1c28, #232338)'
          : 'linear-gradient(135deg, #eef0ff, #e4e8fb)'};
          flex-direction: column;
          gap: 0.6rem;
          color: ${darkMode ? '#5b6ab0' : '#a9b3e8'};
        }

        .project-placeholder-label {
          font-size: 0.68rem;
          font-family: 'Courier Prime', monospace;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .project-image-overlay {
          position: absolute;
          top: 0.75rem;
          right: 0.75rem;
          opacity: 0;
          transition: opacity 0.3s;
          background: rgba(0,0,0,0.5);
          border-radius: 6px;
          padding: 0.3rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        a.project-card:hover .project-image-overlay {
          opacity: 1;
        }

        .project-body {
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
          flex: 1;
        }

        .project-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: ${darkMode ? '#f0f0f0' : '#1a1a1a'};
          line-height: 1.3;
        }

        .project-card.headline .project-title {
          font-size: 1.5rem;
        }

        .project-meta {
          font-size: 0.78rem;
          font-family: 'Courier Prime', monospace;
          color: ${darkMode ? '#8b8b8b' : '#888'};
          line-height: 1.5;
          margin-top: -0.5rem;
        }

        .project-blurb {
          font-size: 0.95rem;
          line-height: 1.7;
          color: ${darkMode ? '#b0b0b0' : '#555'};
        }

        .project-card.headline .project-blurb {
          font-size: 1rem;
        }

        .project-tags {
          margin-top: auto;
          display: flex;
          flex-wrap: wrap;
          gap: 0.4rem;
        }

        .project-tag {
          border-radius: 999px;
          padding: 0.2rem 0.7rem;
          font-size: 0.75rem;
          font-weight: 600;
          background: ${darkMode ? 'rgba(124,147,255,0.12)' : 'rgba(102,126,234,0.08)'};
          color: ${accent};
          border: 1px solid ${darkMode ? 'rgba(124,147,255,0.25)' : 'rgba(102,126,234,0.2)'};
          font-family: 'Courier Prime', monospace;
        }

        .project-link-label {
          font-size: 0.8rem;
          font-family: 'Courier Prime', monospace;
          font-weight: 700;
          color: ${accent};
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
        }

        /* ─── Other work ─── */
        .other-work {
          max-width: 900px;
          margin: 0 auto;
          padding-top: 1rem;
        }

        .other-work-title {
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 1.4px;
          text-transform: uppercase;
          font-family: 'Courier Prime', monospace;
          color: ${darkMode ? '#666' : '#aaa'};
          margin-bottom: 1rem;
          text-align: center;
        }

        .other-work-list {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.5rem 1.5rem;
        }

        .other-work-item {
          font-size: 0.85rem;
          color: ${darkMode ? '#888' : '#888'};
          text-decoration: none;
          transition: color 0.25s;
        }

        .other-work-item:hover {
          color: ${accent};
        }

        .other-work-item strong {
          font-weight: 700;
          color: ${darkMode ? '#aaa' : '#666'};
        }

        /* ─── Resume button ─── */
        .btn-outline {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.85rem 2rem;
          background: transparent;
          color: ${accent};
          text-decoration: none;
          border: 2px solid ${accent};
          border-radius: 8px;
          cursor: pointer;
          font-weight: 700;
          font-size: 1rem;
          font-family: 'Courier Prime', monospace;
          transition: all 0.3s;
          letter-spacing: 0.5px;
        }

        .btn-outline:hover {
          background: ${accent};
          color: #fff;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
        }

        /* ─── Contact ─── */
        .contact-section {
          padding: 5rem 2rem 4rem;
          text-align: center;
        }

        .contact-section .section-heading {
          font-size: 3rem;
        }

        .contact-prompt {
          color: ${darkMode ? '#888' : '#999'};
          font-size: 1rem;
          margin-bottom: 2.5rem;
          font-family: 'Courier Prime', monospace;
        }

        .contact-links {
          display: flex;
          gap: 2.5rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .contact-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }

        .contact-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 64px;
          height: 64px;
          background: ${darkMode ? 'linear-gradient(145deg,#1a1a1a,#1f1f1f)' : '#f5f5f5'};
          color: ${darkMode ? '#e0e0e0' : '#555'};
          text-decoration: none;
          border: 2px solid ${darkMode ? '#3a3a3a' : '#e0e0e0'};
          border-radius: 50%;
          cursor: pointer;
          font-size: 1.5rem;
          transition: all 0.3s;
        }

        .contact-btn:hover {
          border-color: ${accent};
          color: ${accent};
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(102,126,234,0.2);
        }

        .contact-label {
          font-size: 0.8rem;
          font-family: 'Courier Prime', monospace;
          color: ${darkMode ? '#666' : '#aaa'};
        }

        /* ─── Footer ─── */
        .footer {
          padding: 2rem;
          text-align: center;
          border-top: 1px solid ${darkMode ? '#2a2a2a' : '#e8e8e8'};
          font-size: 0.85rem;
          color: ${darkMode ? '#555' : '#bbb'};
          font-family: 'Courier Prime', monospace;
        }

        /* ─── Responsive ─── */
        @media (max-width: 768px) {
          .hero h1 {
            font-size: 2.5rem;
          }

          .hero .subtitle {
            font-size: 1.2rem;
          }

          /* The typewriter's nowrap can't survive a narrow viewport, so drop the
             animation here and let the line wrap normally. */
          .typing-text {
            min-width: auto;
            width: auto;
            max-width: 100%;
            white-space: normal;
            border-right: none;
            animation: none;
          }

          .hero-focus {
            font-size: 0.95rem;
          }

          .hero-line {
            font-size: 1rem;
          }

          .project-card,
          .project-card.headline {
            width: 100%;
            min-width: unset;
          }

          .experience-section,
          .projects-section,
          .awards-section {
            padding-left: 1.25rem;
            padding-right: 1.25rem;
          }

          .award-slide {
            flex-direction: column;
          }

          .award-slide-image {
            width: 100%;
            min-height: 200px;
          }

          .award-nav {
            display: none;
          }
        }
      `}</style>

      {/* Navbar */}
      <nav className="navbar">
        <a href="#about" className="navbar-logo">BB</a>

        <ul className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          {['About', 'Projects', 'Experience', 'Contact'].map(label => (
            <li key={label}>
              <a href={`#${label.toLowerCase()}`} onClick={() => setMobileMenuOpen(false)}>
                {label}
              </a>
            </li>
          ))}
        </ul>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button className="theme-btn" onClick={() => setDarkMode(!darkMode)}>
            {darkMode ? <Sun size={22} color="#fbbf24" /> : <Moon size={22} color="#667eea" />}
          </button>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero" id="about">
        <img src="/assets/mypicture.jpeg" alt="Boubacar Ba" className="profile-picture" />
        <h1>Boubacar Ba</h1>
        <p className="subtitle">
          <span className="typing-text">Embedded Systems Engineer</span>
        </p>
        <p className="hero-focus">
          Robotics &amp; Autonomy · GPS-denied, comms-denied environments
        </p>

        <p className="hero-line">
          I build low-level, real-time software for autonomous systems operating in
          GPS-denied, comms-denied, and infrastructure-free environments.
        </p>
      </section>

      {/* Projects */}
      <section className="projects-section" id="projects">
        <h2 className="section-heading">Past &amp; Present Work</h2>
        <p className="section-subtitle">A selection of things I've built</p>
        <div className="projects-grid">
          {projects.map((project, index) => {
            const Icon = project.icon;
            // Cards without a link render as plain articles so we don't ship
            // an anchor with no href.
            const Tag = project.link ? 'a' : 'article';
            const linkProps = project.link
              ? { href: project.link, target: '_blank', rel: 'noopener noreferrer' }
              : {};

            return (
              <Tag
                key={project.title}
                {...linkProps}
                className={`project-card ${project.headline ? 'headline' : ''} ${animateProjects ? 'animate' : ''}`}
                style={{ animationDelay: `${index * 0.08}s` }}
              >
                {project.image ? (
                  <div className="project-image">
                    <img src={project.image} alt={project.title} />
                    {project.link && (
                      <div className="project-image-overlay">
                        <ExternalLink size={16} color="#fff" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="project-image placeholder">
                    <Icon size={44} strokeWidth={1.25} />
                    <span className="project-placeholder-label">{project.short}</span>
                  </div>
                )}

                <div className="project-body">
                  <div className="project-title">{project.title}</div>
                  <div className="project-meta">{project.meta}</div>

                  <p className="project-blurb">{project.blurb}</p>

                  <div className="project-tags">
                    {project.tags.map(tag => (
                      <span key={tag} className="project-tag">{tag}</span>
                    ))}
                  </div>

                  {project.linkLabel && (
                    <span className="project-link-label">
                      {project.linkLabel} <ExternalLink size={13} />
                    </span>
                  )}
                </div>
              </Tag>
            );
          })}
        </div>

        <div className="other-work">
          <h3 className="other-work-title">Other work</h3>
          <div className="other-work-list">
            {otherWork.map(item => (
              <a
                key={item.name}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="other-work-item"
              >
                <strong>{item.name}</strong> · {item.note}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Experience */}
      <section className="experience-section" id="experience">
        <h2 className="section-heading">Experience</h2>
        <p className="section-subtitle">Where I've shipped</p>
        <div className="experience-list">
          {experience.map(job => (
            <div className="experience-item" key={`${job.org}-${job.period}`}>
              <div className="experience-role">
                {job.role} <span className="experience-org">· {job.org}</span>
              </div>
              <div className="experience-period">{job.period} · {job.location}</div>
              <p className="experience-detail">{job.detail}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Awards */}
      <section className="awards-section" id="awards">
        <h2 className="section-heading">Awards</h2>
        <p className="section-subtitle">Recent recognition</p>
        <div className="awards-carousel">
          <button className="award-nav" onClick={prevAward} aria-label="Previous award">
            <ChevronLeft size={22} />
          </button>
          <div className="awards-viewport">
            <div
              className="awards-track"
              style={{ transform: `translateX(-${awardIndex * 100}%)` }}
            >
              {awards.map(award => {
                const AwardIcon = award.icon;
                return (
                  <div className="award-slide" key={award.event}>
                    {award.image ? (
                      <div className="award-slide-image">
                        <img src={award.image} alt={award.event} />
                      </div>
                    ) : (
                      <div className="award-slide-image placeholder">
                        <AwardIcon size={54} color={award.color} strokeWidth={1.25} />
                        <span className="project-placeholder-label">{award.place}</span>
                      </div>
                    )}
                    <div className="award-slide-body">
                      <div className="award-place">{award.place}</div>
                      <div className="award-event">{award.event}</div>
                      <div className="award-detail">{award.detail}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <button className="award-nav" onClick={nextAward} aria-label="Next award">
            <ChevronRight size={22} />
          </button>
        </div>
        <div className="awards-dots">
          {awards.map((award, i) => (
            <button
              key={award.event}
              className={`award-dot ${i === awardIndex ? 'active' : ''}`}
              onClick={() => setAwardIndex(i)}
              aria-label={`Go to award ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="contact-section" id="contact">
        <h2 className="section-heading">Get In Touch</h2>
        <p className="contact-prompt">I'm always open to new opportunities and collaborations.</p>
        {/* Resume PDF hidden for now
        <div className="hero-actions">
          <a
            href="https://drive.google.com/file/d/1Ed5FDynN9rNTe3BiW6FaCojmLky8Ukmp/view?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline"
          >
            Resume PDF
          </a>
        </div>
        */}
        <div className="contact-links">
          <div className="contact-item">
            <a href="https://github.com/Psoro77" target="_blank" rel="noopener noreferrer" className="contact-btn" title="GitHub">
              <Github size={26} />
            </a>
            <span className="contact-label">GitHub</span>
          </div>
          <div className="contact-item">
            <a href="https://www.linkedin.com/in/b2o-7a9074372/" target="_blank" rel="noopener noreferrer" className="contact-btn" title="LinkedIn">
              <Linkedin size={26} />
            </a>
            <span className="contact-label">LinkedIn</span>
          </div>
          <div className="contact-item">
            <a href="mailto:boubacar@booba.ca" className="contact-btn" title="Email">
              <Mail size={26} />
            </a>
            <span className="contact-label">Email</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        Built by Boubacar Ba &nbsp;·&nbsp; {new Date().getFullYear()}
      </footer>
    </div>
  );
}
